package com.evoapps.evoshift.ui.onboarding

import androidx.compose.animation.AnimatedContent
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.animation.slideInHorizontally
import androidx.compose.animation.togetherWith
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.WindowInsets
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeDrawing
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.windowInsetsPadding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.automirrored.filled.Logout
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Done
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.LinearProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.CardMetallicBrush
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.DarkMetallicBackgroundBrush
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.GoldMetallicBrush
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.FormMaxWidth
import com.evoapps.evoshift.ui.adaptive.LocalWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.ProvideWindowWidthClass
import com.evoapps.evoshift.ui.adaptive.contentWidth
import com.evoapps.evoshift.ui.billing.PlanLimitDialog
import com.evoapps.evoshift.ui.billing.label
import com.evoapps.evoshift.ui.workplaces.SalesTargetsDialog
import com.evoapps.evoshift.domain.model.WorkplaceFinancials
import com.evoapps.evoshift.domain.model.ShiftTemplate
import androidx.compose.material.icons.filled.CameraAlt
import androidx.compose.material.icons.filled.LocationOn
import androidx.compose.material.icons.filled.Notifications
import com.evoapps.evoshift.ui.util.TestTags
import com.evoapps.evoshift.ui.util.asString
import com.evoapps.evoshift.util.AppPermission
import com.evoapps.evoshift.util.rememberPermissionState
import com.evoapps.evoshift.domain.billing.PlanTier
import com.evoapps.evoshift.domain.usecase.StarterScheduleNames
import com.evoapps.evoshift.viewmodels.OnboardingPath
import com.evoapps.evoshift.viewmodels.OnboardingStep
import com.evoapps.evoshift.viewmodels.OnboardingUiState
import com.evoapps.evoshift.viewmodels.OnboardingViewModel
import com.evoapps.evoshift.widgets.GoldButton
import com.evoapps.evoshift.widgets.StandoutCard
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.app_logo
import evoshift.composeapp.generated.resources.availability_slot_all_day
import evoshift.composeapp.generated.resources.back
import evoshift.composeapp.generated.resources.onboarding_done_generic
import evoshift.composeapp.generated.resources.onboarding_done_join
import evoshift.composeapp.generated.resources.onboarding_done_owner
import evoshift.composeapp.generated.resources.onboarding_done_permissions
import evoshift.composeapp.generated.resources.onboarding_done_title
import evoshift.composeapp.generated.resources.onboarding_finishing
import evoshift.composeapp.generated.resources.onboarding_go_to_dashboard
import evoshift.composeapp.generated.resources.onboarding_step_business
import evoshift.composeapp.generated.resources.onboarding_step_counter
import evoshift.composeapp.generated.resources.onboarding_step_done
import evoshift.composeapp.generated.resources.onboarding_step_finance
import evoshift.composeapp.generated.resources.onboarding_step_hours
import evoshift.composeapp.generated.resources.onboarding_step_join
import evoshift.composeapp.generated.resources.onboarding_step_role
import evoshift.composeapp.generated.resources.onboarding_step_welcome
import evoshift.composeapp.generated.resources.onboarding_step_workplace
import evoshift.composeapp.generated.resources.onboarding_summary_plan
import evoshift.composeapp.generated.resources.onboarding_summary_shifts
import evoshift.composeapp.generated.resources.onboarding_summary_workplace
import evoshift.composeapp.generated.resources.onboarding_tagline
import evoshift.composeapp.generated.resources.profile_sign_out
import evoshift.composeapp.generated.resources.setup_camera_desc
import evoshift.composeapp.generated.resources.setup_camera_title
import evoshift.composeapp.generated.resources.setup_location_desc
import evoshift.composeapp.generated.resources.setup_location_title
import evoshift.composeapp.generated.resources.setup_notifications_desc
import evoshift.composeapp.generated.resources.setup_notifications_title
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

/** What the onboarding screen can do; one lambda per user action (stateless content, tests). */
data class OnboardingActions(
    val onStart: () -> Unit = {},
    val onOwner: () -> Unit = {},
    val onJoin: () -> Unit = {},
    val onBack: () -> Unit = {},
    val onSignOut: () -> Unit = {},
    val onBusinessName: (String) -> Unit = {},
    val onSubmitBusiness: () -> Unit = {},
    val onWorkplaceName: (String) -> Unit = {},
    /** With the default availability slot's name in the owner's language (SH-60). */
    val onSubmitWorkplace: (allDaySlotLabel: String) -> Unit = {},
    val onToggleDay: (Int) -> Unit = {},
    val onShiftOpening: (Int) -> Unit = {},
    val onShiftClosing: (Int) -> Unit = {},
    val onSubmitHours: (StarterScheduleNames) -> Unit = {},
    val onSkipHours: () -> Unit = {},
    /** Financial planning (SH-61): yes opens the sales plan, no goes on. */
    val onFinanceAnswer: (Boolean) -> Unit = {},
    val onSaveSalesTargets: (Map<String, Double>) -> Unit = {},
    val onCloseSalesTargets: () -> Unit = {},
    /** Editing the defaults on the review (SH-62). */
    val onUpdateStarterShift: (ShiftTemplate) -> Unit = {},
    val onUpdateOpenDays: (Set<Int>) -> Unit = {},
    val onAddAvailabilitySlot: (label: String, start: String, end: String) -> Unit = { _, _, _ -> },
    val onRemoveAvailabilitySlot: (slotId: String) -> Unit = {},
    /** The workplace review (SH-63). */
    val onRenameWorkplace: (String) -> Unit = {},
    val onEditSalesTargets: () -> Unit = {},
    val onInviteCode: (String) -> Unit = {},
    val onCheckInvite: () -> Unit = {},
    val onJoinTeam: () -> Unit = {},
    val onFinish: () -> Unit = {}
)

/**
 * First run (SH-38), replacing the Setup and the old Onboarding screens: a multi-step flow in
 * EVoLabels' style with EVoShift's navy and gold. A step list beside the content on wide
 * windows, a progress bar above it on phones; back, sign-out and the keyboard's action key work
 * on every step.
 */
@Composable
fun OnboardingScreen(
    viewModel: OnboardingViewModel,
    onFinished: () -> Unit,
    onSeePlans: () -> Unit
) {
    val state by viewModel.state.collectAsState()
    LaunchedEffect(state.isFinished) { if (state.isFinished) onFinished() }

    state.planLimit?.let { limit ->
        PlanLimitDialog(
            limit = limit,
            onSeePlans = {
                viewModel.planLimitShown()
                onSeePlans()
            },
            onDismiss = viewModel::planLimitShown
        )
    }

    OnboardingContent(
        state = state,
        actions = OnboardingActions(
            onStart = viewModel::start,
            onOwner = viewModel::chooseOwner,
            onJoin = viewModel::chooseJoin,
            onBack = viewModel::back,
            onSignOut = viewModel::signOut,
            onBusinessName = viewModel::updateBusinessName,
            onSubmitBusiness = viewModel::submitBusiness,
            onWorkplaceName = viewModel::updateWorkplaceName,
            onSubmitWorkplace = viewModel::submitWorkplace,
            onToggleDay = viewModel::toggleDay,
            onShiftOpening = viewModel::shiftOpening,
            onShiftClosing = viewModel::shiftClosing,
            onSubmitHours = viewModel::submitHours,
            onSkipHours = viewModel::skipHours,
            onFinanceAnswer = viewModel::answerFinancePlanning,
            onSaveSalesTargets = viewModel::submitSalesTargets,
            onCloseSalesTargets = viewModel::closeSalesTargets,
            onUpdateStarterShift = viewModel::updateStarterShift,
            onUpdateOpenDays = viewModel::updateOpenDays,
            onAddAvailabilitySlot = viewModel::addAvailabilitySlot,
            onRemoveAvailabilitySlot = viewModel::removeAvailabilitySlot,
            onRenameWorkplace = viewModel::renameWorkplace,
            onEditSalesTargets = viewModel::editSalesTargets,
            onInviteCode = viewModel::updateInviteCode,
            onCheckInvite = viewModel::checkInvite,
            onJoinTeam = viewModel::join,
            onFinish = viewModel::finish
        ),
        onErrorShown = viewModel::errorShown,
        permissions = { RecommendedPermissions() }
    )
}

@Composable
fun OnboardingContent(
    state: OnboardingUiState,
    actions: OnboardingActions,
    onErrorShown: () -> Unit = {},
    permissions: @Composable () -> Unit = {}
) {
    val snackbar = remember { SnackbarHostState() }
    val errorText = state.error?.asString()
    LaunchedEffect(errorText) {
        if (errorText != null) {
            snackbar.showSnackbar(errorText)
            onErrorShown()
        }
    }
    ProvideWindowWidthClass(Modifier.fillMaxSize().background(DarkMetallicBackgroundBrush)) { widthClass ->
        // SH-245: what is drawn straight on the gradient takes its light roles.
        com.evoapps.evoshift.widgets.ProvideGradientColors {
        Row(Modifier.fillMaxSize().windowInsetsPadding(WindowInsets.safeDrawing)) {
            if (widthClass.isExpanded && !state.isLoading) StepRail(state)
            Column(Modifier.weight(1f).fillMaxHeight()) {
                TopBar(state, actions, showBrand = !widthClass.isExpanded)
                if (state.isLoading) {
                    Box(Modifier.fillMaxSize(), contentAlignment = Alignment.Center) { CircularProgressIndicator(color = DarkGold) }
                } else {
                    Column(
                        Modifier
                            .fillMaxSize()
                            .verticalScroll(rememberScrollState())
                            .contentWidth(FormMaxWidth)
                            .padding(horizontal = 24.dp, vertical = 16.dp)
                    ) {
                        AnimatedContent(
                            targetState = state.step,
                            transitionSpec = {
                                val forward = state.steps.indexOf(targetState) >= state.steps.indexOf(initialState)
                                (slideInHorizontally { width -> if (forward) width / 6 else -width / 6 } + fadeIn()) togetherWith fadeOut()
                            }
                        ) { step ->
                            StepContent(step, state, actions, permissions)
                        }
                    }
                }
            }
        }
        }
        SnackbarHost(snackbar, Modifier.align(Alignment.BottomCenter).windowInsetsPadding(WindowInsets.safeDrawing))
        // The weekly sales plan: the workplace settings' editor, saved under the day keys (SH-61).
        if (state.isEditingSalesTargets) {
            SalesTargetsDialog(
                initialTargets = WorkplaceFinancials(salesTargets = state.salesTargets),
                currency = state.market.currency,
                onDismiss = actions.onCloseSalesTargets,
                onSave = actions.onSaveSalesTargets
            )
        }
    }
}

@Composable
private fun StepContent(
    step: OnboardingStep,
    state: OnboardingUiState,
    actions: OnboardingActions,
    permissions: @Composable () -> Unit
) {
    when (step) {
        OnboardingStep.WELCOME -> WelcomeStep(actions.onStart)
        OnboardingStep.ROLE -> RoleStep(actions.onOwner, actions.onJoin)
        OnboardingStep.BUSINESS -> BusinessStep(state.businessName, state.fieldError, state.isSaving, actions.onBusinessName, actions.onSubmitBusiness)
        OnboardingStep.WORKPLACE -> {
            val allDaySlotLabel = stringResource(Res.string.availability_slot_all_day)
            WorkplaceStep(state.workplaceName, state.market, state.fieldError, state.isSaving, actions.onWorkplaceName) {
                actions.onSubmitWorkplace(allDaySlotLabel)
            }
        }
        OnboardingStep.HOURS -> HoursStep(
            hours = state.hours,
            error = state.fieldError,
            isSaving = state.isSaving,
            onToggleDay = actions.onToggleDay,
            onShiftOpening = actions.onShiftOpening,
            onShiftClosing = actions.onShiftClosing,
            onNext = actions.onSubmitHours,
            onSkip = actions.onSkipHours
        )
        OnboardingStep.FINANCE -> FinanceStep(actions.onFinanceAnswer)
        OnboardingStep.JOIN -> JoinStep(state.inviteCode, state.invite, state.fieldError, state.isSaving, actions.onInviteCode, actions.onCheckInvite, actions.onJoinTeam)
        OnboardingStep.DONE -> DoneStep(state, actions.onFinish, permissions, actions)
    }
}

@Composable
private fun TopBar(state: OnboardingUiState, actions: OnboardingActions, showBrand: Boolean) {
    Column(Modifier.fillMaxWidth().padding(horizontal = 8.dp, vertical = 4.dp)) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            Box(Modifier.weight(1f)) {
                if (state.canGoBack) {
                    TextButton(onClick = actions.onBack, modifier = Modifier.testTag(TestTags.ONBOARDING_BACK)) {
                        Icon(Icons.AutoMirrored.Filled.ArrowBack, contentDescription = null, tint = Color.White, modifier = Modifier.size(18.dp))
                        Spacer(Modifier.width(6.dp))
                        Text(stringResource(Res.string.back), color = Color.White)
                    }
                }
            }
            if (showBrand) {
                Image(painterResource(Res.drawable.app_logo), contentDescription = "EVoShift", modifier = Modifier.size(32.dp))
            }
            Box(Modifier.weight(1f), contentAlignment = Alignment.CenterEnd) {
                TextButton(onClick = actions.onSignOut, modifier = Modifier.testTag(TestTags.ONBOARDING_SIGN_OUT)) {
                    Text(stringResource(Res.string.profile_sign_out), color = Color.White.copy(alpha = 0.75f))
                    Spacer(Modifier.width(6.dp))
                    Icon(Icons.AutoMirrored.Filled.Logout, contentDescription = null, tint = Color.White.copy(alpha = 0.75f), modifier = Modifier.size(18.dp))
                }
            }
        }
        if (!state.isLoading && state.steps.size > 1) {
            Column(Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                LinearProgressIndicator(
                    progress = { state.progress },
                    color = DarkGold,
                    trackColor = Color.White.copy(alpha = 0.15f),
                    strokeCap = StrokeCap.Round,
                    modifier = Modifier.fillMaxWidth().height(6.dp).testTag(TestTags.ONBOARDING_PROGRESS)
                )
                Text(
                    "${stringResource(Res.string.onboarding_step_counter, state.stepNumber, state.steps.size)} · ${state.step.title()}",
                    color = Color.White.copy(alpha = 0.7f),
                    style = MaterialTheme.typography.labelMedium
                )
            }
        }
    }
}

/** Wide windows: the brand and every step of the path, done ones ticked. */
@Composable
private fun StepRail(state: OnboardingUiState) {
    Column(
        Modifier.width(300.dp).fillMaxHeight().background(CardMetallicBrush).padding(28.dp),
        verticalArrangement = Arrangement.spacedBy(16.dp)
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            Image(painterResource(Res.drawable.app_logo), contentDescription = null, modifier = Modifier.size(44.dp))
            Spacer(Modifier.width(12.dp))
            Text("EVoShift", style = MaterialTheme.typography.headlineSmall, color = LightGold, fontWeight = FontWeight.Bold)
        }
        Text(stringResource(Res.string.onboarding_tagline), color = Color.White.copy(alpha = 0.75f), style = MaterialTheme.typography.bodyMedium)
        Spacer(Modifier.height(8.dp))
        val current = state.steps.indexOf(state.step)
        state.steps.forEachIndexed { index, step ->
            val done = index < current
            val active = index == current
            Row(verticalAlignment = Alignment.CenterVertically) {
                Box(
                    Modifier.size(28.dp).background(
                        brush = if (done || active) GoldMetallicBrush else CardMetallicBrush,
                        shape = CircleShape
                    ),
                    contentAlignment = Alignment.Center
                ) {
                    if (done) Icon(Icons.Default.Check, null, tint = DeepNavy, modifier = Modifier.size(16.dp))
                    else Text("${index + 1}", color = if (active) DeepNavy else Color.White, style = MaterialTheme.typography.labelMedium, fontWeight = FontWeight.Bold)
                }
                Spacer(Modifier.width(12.dp))
                Text(
                    step.title(),
                    color = if (active) LightGold else Color.White.copy(alpha = if (done) 0.85f else 0.55f),
                    fontWeight = if (active) FontWeight.Bold else FontWeight.Normal
                )
            }
        }
    }
}

@Composable
private fun OnboardingStep.title(): String = stringResource(
    when (this) {
        OnboardingStep.WELCOME -> Res.string.onboarding_step_welcome
        OnboardingStep.ROLE -> Res.string.onboarding_step_role
        OnboardingStep.BUSINESS -> Res.string.onboarding_step_business
        OnboardingStep.WORKPLACE -> Res.string.onboarding_step_workplace
        OnboardingStep.HOURS -> Res.string.onboarding_step_hours
        OnboardingStep.FINANCE -> Res.string.onboarding_step_finance
        OnboardingStep.JOIN -> Res.string.onboarding_step_join
        OnboardingStep.DONE -> Res.string.onboarding_step_done
    }
)

@Composable
fun DoneStep(
    state: OnboardingUiState,
    onFinish: () -> Unit,
    permissions: @Composable () -> Unit = {},
    actions: OnboardingActions = OnboardingActions()
) {
    Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally, verticalArrangement = Arrangement.spacedBy(18.dp)) {
        Box(Modifier.size(88.dp).background(GoldMetallicBrush, CircleShape), contentAlignment = Alignment.Center) {
            Icon(Icons.Default.Done, contentDescription = null, tint = DeepNavy, modifier = Modifier.size(48.dp))
        }
        Text(
            stringResource(Res.string.onboarding_done_title),
            style = MaterialTheme.typography.headlineMedium,
            color = LightGold,
            fontWeight = FontWeight.Bold,
            textAlign = TextAlign.Center
        )
        val workplace = state.createdWorkplaceName
        val joined = state.joinedWorkplaceName
        Text(
            when {
                state.path == OnboardingPath.OWNER && workplace != null -> stringResource(Res.string.onboarding_done_owner, workplace)
                state.path == OnboardingPath.JOIN && joined != null -> stringResource(Res.string.onboarding_done_join, joined)
                else -> stringResource(Res.string.onboarding_done_generic)
            },
            color = Color.White.copy(alpha = 0.85f),
            textAlign = TextAlign.Center
        )
        if (state.path == OnboardingPath.OWNER && workplace != null && state.workplace != null) {
            // The workplace's settings to check, each default with its pencil (SH-62, SH-63).
            OnboardingWorkplaceReview(state, actions)
            OnboardingDefaultsReview(state, actions)
        } else if (state.path == OnboardingPath.OWNER && workplace != null) {
            StandoutCard(Modifier.fillMaxWidth()) {
                Column(Modifier.padding(16.dp), verticalArrangement = Arrangement.spacedBy(10.dp)) {
                    SummaryLine(stringResource(Res.string.onboarding_summary_workplace))
                    if (state.starterScheduleCreated) SummaryLine(stringResource(Res.string.onboarding_summary_shifts))
                    SummaryLine(stringResource(Res.string.onboarding_summary_plan, PlanTier.TRIAL.label()))
                }
            }
        }
        permissions()
        GoldButton(
            stringResource(Res.string.onboarding_go_to_dashboard),
            onFinish,
            Modifier.fillMaxWidth().testTag(TestTags.ONBOARDING_FINISH),
            enabled = state.isReady,
            isBusy = state.isSaving
        )
        if (!state.isReady) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                SmallProgress()
                Spacer(Modifier.width(8.dp))
                Text(stringResource(Res.string.onboarding_finishing), color = Color.White.copy(alpha = 0.7f))
            }
        }
    }
}

/**
 * Notifications, the one permission the app uses; only shown while missing. Location and camera
 * are not asked for: nothing uses them (data minimisation, SH-50), and the manifests declare
 * neither.
 */
@Composable
private fun RecommendedPermissions() {
    val notifications = rememberPermissionState(AppPermission.NOTIFICATIONS)
    if (notifications.isGranted) return
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(10.dp)) {
        Text(stringResource(Res.string.onboarding_done_permissions), color = LightGold, style = MaterialTheme.typography.titleSmall)
        PermissionItem(
            icon = Icons.Default.Notifications,
            title = stringResource(Res.string.setup_notifications_title),
            description = stringResource(Res.string.setup_notifications_desc),
            isGranted = notifications.isGranted,
            onClick = notifications::launchPermissionRequest
        )
    }
}
